import React, {useState} from 'react';
import {ChevronDown, ChevronUp, Tag} from 'lucide-react';
import {publicPost} from '../services/publicClient';
import Button from '@/shared/components/Button';

interface PromoCodeFieldProps {
    subtotalCents: number;
    onApply: (code: string, discountCents: number) => void;
    onClear: () => void;
    appliedCode: string | null;
    appliedDiscount: number;
}

const PromoCodeField: React.FC<PromoCodeFieldProps> = ({subtotalCents, onApply, onClear, appliedCode, appliedDiscount}) => {
    const [isOpen, setIsOpen] = useState(!!appliedCode);
    const [code, setCode] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const handleApply = async () => {
        if (!code.trim()) return;
        setError('');
        setLoading(true);
        try {
            const result = await publicPost<{valid: boolean; discountCents: number; code: string}>(
                '/api/shop/promo/validate',
                {code: code.trim(), subtotalCents},
            );
            onApply(result.code, result.discountCents);
            setCode('');
        } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : 'Invalid promo code';
            setError(msg);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div>
            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                className="flex items-center gap-2 text-sm text-mld-text-primary font-medium hover:underline">
                <Tag className="h-4 w-4" />
                Add a promo code
                {isOpen ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
            </button>

            {isOpen && (
                <div className="mt-2">
                    {appliedCode ? (
                        <div className="flex items-center justify-between bg-mld-success/10 rounded-lg px-3 py-2">
                            <span className="text-sm text-mld-text-primary font-medium">
                                {appliedCode} – ${(appliedDiscount / 100).toFixed(2)} off
                            </span>
                            <button type="button" onClick={onClear} className="text-xs text-mld-danger hover:underline">
                                Remove
                            </button>
                        </div>
                    ) : (
                        <>
                            <div className="flex gap-2">
                                <input
                                    type="text"
                                    value={code}
                                    onChange={(e) => setCode(e.target.value.toUpperCase())}
                                    placeholder="Enter code"
                                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-mld-success/50"
                                />
                                <Button
                                    type="button"
                                    variant="primary"
                                    size="md"
                                    onClick={handleApply}
                                    disabled={!code.trim()}
                                    loading={loading}>
                                    Apply
                                </Button>
                            </div>
                            {error && <p className="text-xs text-mld-danger mt-1">{error}</p>}
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export default PromoCodeField;
